import { useCallback } from "react";
import { Box, Grid, Typography, styled } from "~/common/mui";
import { Card } from "~/features/configurator/Linear/components/Card";
import { Controller, useFormContext } from "react-hook-form";
import {
  type CheckboxSelectProps,
  isActive,
  type RHFCheckboxSelectProps,
  useOnElementClick,
} from "~/features/selectionAssistant/housing/components/Inputs/CheckboxSelect/CheckboxSelect";
import { useHSAFacetData } from "~/features/selectionAssistant/housing/components/useHSAFacetData";
import {
  type ItemDefinitionV2StateData,
  type TileDefinitionV2,
} from "~/features/selectionAssistant/housing/types/FilterDefinitionV2";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";

export interface TileSelectProps
  extends Omit<CheckboxSelectProps<TileDefinitionV2 & ItemDefinitionV2StateData>, "variant"> {
  variant?: TileSelectVariant;
}

export enum TileSelectVariant {
  ICN_TOP = "img-top",
  ICN_LEFT = "img-left",
}

const TileLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.titleSmall,
}));

const TileLabelLarge = styled(Typography)(({ theme }) => ({
  ...theme.typography.labelLarge,
}));

const ImgTopTile = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

const ImgLeftTile = styled(Card)<{ tileVariant: string }>(({ theme, tileVariant }) => ({
  display: "flex",
  position: "relative",
  flexDirection: "column",
  padding: theme.spacing(1.5),
  [theme.breakpoints.up("desktop-s")]: {
    padding: "0 16px 0 0",
    flexDirection: "row",
  },
}));

const ImgLeftTileImage = styled("img")({
  width: "50px",
  height: "50px",
  flex: "none",
});

export const TileSelect = ({
  className,
  label,
  value,
  values,
  multiple,
  onChange,
  "data-test-id": testId,
  variant = TileSelectVariant.ICN_TOP,
}: TileSelectProps) => {
  const onTileClick = useOnElementClick(value, onChange, multiple);

  const gridColSize = variant === TileSelectVariant.ICN_TOP ? 4 : 12;

  return (
    <Box className={className} data-test-id={testId}>
      {label && (
        <Typography variant="labelLarge" sx={{ fontWeight: "bold", mb: 2.5 }}>{label}</Typography>
      )}
      <Grid container sx={{ mt: -3.75, ml: -4, width: "auto !important", "& > *": { pt: 3.75, pl: 4 } }}>
        {values.map((tileDef) => (
          <Grid
            key={tileDef.code}
            size={{
              phone: gridColSize,
            }}
          >
            <Tile
              {...tileDef}
              variant={variant}
              selected={isActive(value, tileDef.code)}
              onClick={onTileClick}
              data-test-id={`${testId}-${tileDef.code}`}
              disabled={tileDef.disabled && !isActive(value, tileDef.code)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

interface TileProps extends TileDefinitionV2, ItemDefinitionV2StateData {
  onClick: (code: string) => void;
  "data-test-id"?: string;
  variant: TileSelectVariant;
  selected?: boolean;
}

const ImgLeftTileContent = styled(Box)(({ theme }) => ({
  margin: "4px 0 0",
  padding: 0,
  [theme.breakpoints.up("desktop-s")]: {
    padding: "11px 0",
    margin: "0 auto 0 0",
    display: "flex",
    alignItems: "center",
  },
}));

const ImgLeftCheck = styled(CheckedCircle)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1.5),
  top: theme.spacing(1.5),
  [theme.breakpoints.up("desktop-s")]: {
    position: "initial",
    alignSelf: "center",
    marginLeft: theme.spacing(2),
    flex: "none",
  },
}));

const ImgTopCheck = styled(CheckedCircle)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
}));

const Tile = ({
  selected,
  disabled,
  activeImageUrl,
  imageUrl,
  label,
  code,
  onClick,
  variant,
  "data-test-id": testId,
}: TileProps) => {
  const onClickHandler = useCallback(() => onClick(code), [onClick, code]);

  const img = selected && activeImageUrl ? activeImageUrl : imageUrl;

  if (variant === TileSelectVariant.ICN_LEFT) {
    return (
      <ImgLeftTile tileVariant={variant} disabled={disabled} onClick={onClickHandler} active={selected} data-test-id={testId}>
        {img && (
          <Box sx={{ p: 0 }}>
            <ImgLeftTileImage src={img} alt={""} />
          </Box>
        )}
        <ImgLeftTileContent>
          <TileLabelLarge>{label}</TileLabelLarge>
        </ImgLeftTileContent>
        <ImgLeftCheck checked={selected} />
      </ImgLeftTile>
    );
  }

  return (
    <ImgTopTile disabled={disabled} onClick={onClickHandler} active={selected} data-test-id={testId}>
      {img && (
        <Box sx={{ pt: 1.25, pb: 1.25 }}>
          <Box component="img" src={img} alt={""} sx={{ height: "86px" }} />
        </Box>
      )}
      <Box sx={{ mt: "auto", p: "20px 16px" }}>
        <TileLabel>{label}</TileLabel>
      </Box>
      <ImgTopCheck checked={selected} />
    </ImgTopTile>
  );
};

export interface RHFTileSelectProps extends Omit<RHFCheckboxSelectProps, "columnAmount"> {
  variant?: TileSelectVariant;
  renderGA?: (value: unknown) => void;
}

export const RHFTileSelect = ({ name, renderGA, ...rest }: RHFTileSelectProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      render={({ field: { value, onChange } }) => (
        <TileSelect
          value={value}
          onChange={(e) => {
            onChange(e);
            name === "housing_type" && renderGA && renderGA(e);
          }}
          {...rest}
        />
      )}
      control={control}
      name={name}
    />
  );
};

export interface HousingSATileSelectProps extends Omit<RHFTileSelectProps, "values" | "label"> {}

export const HousingSATileSelect = (props: HousingSATileSelectProps) => {
  const { values, label } = useHSAFacetData(props);

  return <RHFTileSelect values={values} label={label} {...props} />;
};
